// SKRAĆENO OBJAŠNJENJE:
// - nema više scroll cele stranice
// - levi panel = search + lista
// - desni panel = tabovi (Details / Preview / Usage / Jobs)
// - sve profesionalno raspoređeno

import React, { useEffect, useMemo, useState, useRef  } from "react";
import { useNavigate } from "react-router-dom";
import interiorCatalogApi from "../services/interiorCatalogApi";
import { Canvas } from "@react-three/fiber";
import { Grid, OrbitControls, GizmoHelper, GizmoViewport } from "@react-three/drei";
import GeneratedAssetMesh from "../components/asset-geometry/GeneratedAssetMesh";

/* ---------- UTIL ---------- */

function statusBadgeClass(status) {
  switch (String(status || "").toLowerCase()) {
    case "ready":
      return "bg-green-100 text-green-700";
    case "queued":
      return "bg-yellow-100 text-yellow-700";
    case "processing":
      return "bg-blue-100 text-blue-700";
    case "failed":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function getFileTypeLabel(type) {
  const v = String(type || "").toLowerCase();
  if (v === "step" || v === "stp") return "STEP";
  if (v === "glb" || v === "gltf") return "GLB";
  return v || "—";
}

function ModelViewer({ url }) {
  if (!url) return null;

  const fullUrl = String(url).startsWith("http")
    ? url
    : `https://modularobjects.com${url}`;

  return (
    <div className="border rounded bg-slate-900 p-3">
      <div className="text-md font-semibold mb-2 text-white">3D Preview</div>

      <model-viewer
        src={fullUrl}
        camera-controls
        auto-rotate
        exposure="0.55"
        shadow-intensity="1"
        shadow-softness="0.8"
        environment-image="neutral"
        camera-orbit="45deg 65deg auto"
        style={{
          width: "100%",
          height: "520px",
          background: "#111827",
          borderRadius: "8px",
        }}
      />

      <div className="mt-2 text-xs text-slate-300 break-all">{fullUrl}</div>
    </div>
  );
}

function GeneratedAssetViewer({ asset }) {
  const geometry = asset?.metadata_json?.geometry;
  const controlsRef = useRef(null);

  if (!geometry) return null;

  return (
    <div className="border rounded bg-slate-900 p-3">
      <div className="text-md font-semibold mb-2 text-white">
        Generated Geometry Preview
      </div>

      <div className="h-[520px] rounded overflow-hidden bg-slate-100">
        <Canvas camera={{ position: [140, 120, 180], fov: 45 }}>
          <color attach="background" args={["#f3f4f6"]} />
          <ambientLight intensity={1.1} />
          <directionalLight position={[300, 500, 300]} intensity={1.2} />

          <Grid
            args={[1000, 1000]}
            cellSize={10}
            sectionSize={50}
            fadeDistance={3000}
            infiniteGrid
          />

          <GeneratedAssetMesh geometry={geometry} showAxes />

          <OrbitControls
  ref={controlsRef}
  makeDefault
  enableDamping={false}
  dampingFactor={0}
  rotateSpeed={0.65}
  zoomSpeed={0.8}
  panSpeed={0.8}
/>

          <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
            <GizmoViewport />
          </GizmoHelper>
        </Canvas>
      </div>

      <pre className="mt-2 text-xs text-slate-300 overflow-auto">
        {JSON.stringify(geometry, null, 2)}
      </pre>
    </div>
  );
}


/* ---------- PAGE ---------- */

export default function InteriorAssetsPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);

const [selectedAssetStatus, setSelectedAssetStatus] = useState(null);
const [selectedAssetUsage, setSelectedAssetUsage] = useState(null);

  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [detailsTab, setDetailsTab] = useState("details");

  /* ---------- LOAD ---------- */


async function handleSelectAsset(item) {
  setSelectedAsset(item);
  setSelectedAssetStatus(null);
  setSelectedAssetUsage(null);

  await Promise.all([
    loadAssetStatus(item.id),
    loadAssetUsage(item.id),
  ]);
}

  async function loadAssets(nextOffset = 0) {
    try {
      setLoading(true);

      const res = await interiorCatalogApi.listAssets({
        search: filterText,
        status: statusFilter,
        asset_type: typeFilter,
        limit,
        offset: nextOffset,
      });

      const list = Array.isArray(res)
  ? res
  : Array.isArray(res?.items)
    ? res.items
    : Array.isArray(res?.data?.items)
      ? res.data.items
      : [];

setItems(list);
setTotalCount(
  Array.isArray(res)
    ? res.length
    : Number(res?.total || res?.totalCount || res?.data?.total || list.length || 0)
);
      setOffset(nextOffset);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function loadAssetStatus(assetId) {
  try {
    const res = await interiorCatalogApi.getAssetStatus(assetId);
    setSelectedAssetStatus(res);
  } catch (err) {
    console.error("loadAssetStatus error:", err);
    setSelectedAssetStatus(null);
  }
}

async function loadAssetUsage(assetId) {
  try {
    const res = await interiorCatalogApi.getAssetUsage(assetId);
    setSelectedAssetUsage(res);
  } catch (err) {
    console.error("loadAssetUsage error:", err);
    setSelectedAssetUsage(null);
  }
}

async function handleSelectAsset(item) {
  setSelectedAsset(item);
  setSelectedAssetStatus(null);
  setSelectedAssetUsage(null);

  await Promise.all([
    loadAssetStatus(item.id),
    loadAssetUsage(item.id),
  ]);
}

  useEffect(() => {
    loadAssets(0);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadAssets(0), 400);
    return () => clearTimeout(t);
  }, [filterText, statusFilter, typeFilter]);

  /* ---------- FILTER ---------- */

  const filtered = useMemo(() => {
  const safeItems = Array.isArray(items) ? items : [];

  return safeItems.filter((a) =>
    String(a.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
    String(a.code || "").toLowerCase().includes(filterText.toLowerCase())
  );
}, [items, filterText]);

  /* ---------- UI ---------- */

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col overflow-hidden bg-slate-100">
      {/* HEADER */}
      <div className="h-14 border-b bg-white px-3 flex items-center justify-between">
        <div className="text-sm font-semibold">Interior Assets</div>

        <div className="flex gap-2">
          <button type="button"
  disabled={!selectedAsset?.id}
  onClick={() =>
    selectedAsset?.id &&
    navigate(`/interior-editor/assets/${selectedAsset.id}/mounting`)
  }
  className="px-3 py-1.5 rounded border bg-white text-sm disabled:opacity-40"
>
  Mounting
</button>
          <button onClick={() => navigate("/interior-editor/library")} className="px-3 py-1 border">Library</button>
          <button onClick={() => navigate("/interior-editor/catalog")} className="px-3 py-1 border">Catalog</button>
          <button onClick={() => navigate("/interior-editor/projects")} className="px-3 py-1 border">Projects</button>
          <button onClick={() => loadAssets(offset)} className="px-3 py-1 border">Refresh</button>
        </div>
      </div>

      {/* MAIN */}
      <div className="flex-1 grid grid-cols-[320px_minmax(0,1fr)] overflow-hidden">
        
        {/* LEFT PANEL */}
        <aside className="border-r bg-white flex flex-col min-h-0">

          {/* SEARCH */}
          <div className="p-3 border-b space-y-2">
            <input
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1 border rounded"
            />

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border px-2 py-1 rounded"
            >
              <option value="">All statuses</option>
              <option value="ready">Ready</option>
              <option value="queued">Queued</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full border px-2 py-1 rounded"
            >
              <option value="">All types</option>
              <option value="step">STEP</option>
              <option value="glb">GLB</option>
            </select>
          </div>

          {/* LIST */}
          <div className="flex-1 overflow-auto p-2 space-y-2">
            {loading ? (
              <div>Loading...</div>
            ) : filtered.length ? (
              filtered.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSelectAsset(item)}
                  className={`p-2 border rounded cursor-pointer ${
                    selectedAsset?.id === item.id ? "bg-blue-50 border-blue-300" : ""
                  }`}
                >
                  <div className="font-medium text-sm">{item.name}</div>
                  <div className="text-xs text-gray-500">{item.code}</div>

                  <div className="flex gap-2 mt-1">
                    <span className="text-xs px-1 bg-gray-100">
                      {getFileTypeLabel(item.asset_type)}
                    </span>
                    <span className={`text-xs px-1 ${statusBadgeClass(item.conversion_status)}`}>
                      {item.conversion_status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div>No assets</div>
            )}
          </div>

          {/* PAGING */}
          <div className="p-2 border-t flex justify-between text-xs">
            <button disabled={offset <= 0} onClick={() => loadAssets(offset - limit)}>Prev</button>
            <div>{offset + 1} - {offset + items.length}</div>
            <button onClick={() => loadAssets(offset + limit)}>Next</button>
          </div>
        </aside>

        {/* RIGHT PANEL */}
        <main className="flex flex-col overflow-hidden">

          {/* TABS */}
          <div className="h-10 border-b bg-white flex">
            {["details", "preview", "usage", "jobs"].map((t) => (
              <button
                key={t}
                onClick={() => setDetailsTab(t)}
                className={`px-4 text-sm ${
                  detailsTab === t ? "bg-blue-50 text-blue-700" : ""
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* CONTENT */}
          <div className="flex-1 overflow-auto p-4">
            {!selectedAsset ? (
              <div>Select asset</div>
            ) : (
              <>
                {detailsTab === "details" && (
  <div className="grid grid-cols-2 gap-4 text-sm">
    <div>
      <div className="text-slate-500">Name</div>
      <div className="font-medium">{selectedAsset.name}</div>
    </div>

    <div>
      <div className="text-slate-500">Code</div>
      <div className="font-medium">{selectedAsset.code}</div>
    </div>

    <div>
      <div className="text-slate-500">Type</div>
      <div>{getFileTypeLabel(selectedAsset.asset_type)}</div>
    </div>

    <div>
      <div className="text-slate-500">Status</div>
      <span className={`text-xs px-2 py-1 rounded ${statusBadgeClass(selectedAsset.conversion_status)}`}>
        {selectedAsset.conversion_status}
      </span>
    </div>

    <div className="col-span-2">
      <div className="text-slate-500">Source file</div>
      <div className="font-mono text-xs break-all">{selectedAsset.source_file_path || "—"}</div>
    </div>

    <div className="col-span-2">
      <div className="text-slate-500">Converted file</div>
      <div className="font-mono text-xs break-all">{selectedAsset.converted_file_path || "—"}</div>
    </div>

    <div className="col-span-2">
      <div className="text-slate-500">Message</div>
      <div>{selectedAsset.conversion_message || "—"}</div>
    </div>
  </div>
)}

                {detailsTab === "preview" && (
  selectedAsset?.converted_file_path ? (
    <ModelViewer url={selectedAsset.converted_file_path} />
  ) : selectedAsset?.metadata_json?.geometry ? (
    <GeneratedAssetViewer asset={selectedAsset} />
  ) : (
    <div className="text-slate-500">
      No converted GLB or generated geometry available.
    </div>
  )
)}

                {detailsTab === "usage" && (
  <div>
    {selectedAssetUsage?.summary ? (
      <div className="text-sm text-slate-500 mb-3">
        {selectedAssetUsage.summary.item_count} items ·{" "}
        {selectedAssetUsage.summary.family_count} families
      </div>
    ) : null}

    {selectedAssetUsage?.items?.length ? (
      <div className="border rounded overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left p-2 border-b">Item</th>
              <th className="text-left p-2 border-b">SKU</th>
              <th className="text-left p-2 border-b">Brand</th>
              <th className="text-left p-2 border-b">Family</th>
              <th className="text-left p-2 border-b">Role</th>
            </tr>
          </thead>
          <tbody>
            {selectedAssetUsage.items.map((row, idx) => (
              <tr key={`${row.item_id}_${row.family_id}_${idx}`} className="border-b">
                <td className="p-2">
                  <div className="font-medium">{row.item_name || "—"}</div>
                  <div className="text-xs text-slate-500">{row.item_code || "—"}</div>
                </td>
                <td className="p-2">{row.sku || "—"}</td>
                <td className="p-2">{row.brand || row.manufacturer || "—"}</td>
                <td className="p-2">
                  <div>{row.family_name || "—"}</div>
                  <div className="text-xs text-slate-500">{row.family_code || "—"}</div>
                </td>
                <td className="p-2">{row.asset_role || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="text-slate-500">No usage links found.</div>
    )}
  </div>
)}

                {detailsTab === "jobs" && (
  <div className="space-y-2">
    {selectedAssetStatus?.jobs?.length ? (
      selectedAssetStatus.jobs.map((job) => (
        <div key={job.id} className="border rounded p-3 bg-white">
          <div className="flex items-center justify-between">
            <div className="font-medium">
              Job #{job.id} · {job.job_type}
            </div>
            <span className={`text-xs px-2 py-1 rounded ${statusBadgeClass(job.status)}`}>
              {job.status}
            </span>
          </div>

          <div className="text-xs text-slate-500 mt-1">
            attempts: {job.attempts} · created: {job.created_at}
          </div>

          <pre className="text-xs bg-slate-50 p-2 rounded overflow-auto mt-2">
            {JSON.stringify(job.result_json || job.payload_json || {}, null, 2)}
          </pre>
        </div>
      ))
    ) : (
      <div className="text-slate-500">No jobs yet.</div>
    )}
  </div>
)}
              </>
            )}
          </div>
        </main>
      </div>

      {/* FOOTER */}
      <div className="h-6 border-t bg-white text-xs flex justify-between px-2">
        <div>Assets: {items.length}</div>
        <div>{selectedAsset?.name || "None selected"}</div>
      </div>
    </div>
  );
}